import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Search,
  Filter,
  Eye,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Wrench,
  FileText,
  Building,
  ClipboardList,
  X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FormSubmissions = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');

  useEffect(() => {
    fetchSubmissions();
  }, [filterType, filterStatus]);

  const fetchSubmissions = async () => {
    try {
      let url = `${API}/forms/submissions`;
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('form_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setSubmissions(response.data);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!selectedSubmission || !reviewStatus) return;
    
    try {
      await axios.put(`${API}/forms/submissions/${selectedSubmission.submission_id}/review`, {
        status: reviewStatus,
        notes: reviewNotes
      }, { withCredentials: true });
      
      toast.success("Submission updated successfully");
      setSelectedSubmission(null);
      setReviewNotes('');
      setReviewStatus('');
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to update submission");
    }
  };

  const getFormIcon = (formType) => {
    switch (formType) {
      case 'incident': return <AlertTriangle className="w-5 h-5" />;
      case 'equipment_inspection': return <Wrench className="w-5 h-5" />;
      case 'job_hazard_analysis': return <FileText className="w-5 h-5" />;
      case 'jobsite_inspection': return <Building className="w-5 h-5" />;
      default: return <ClipboardList className="w-5 h-5" />;
    }
  };

  const getFormColor = (formType) => {
    switch (formType) {
      case 'incident': return 'bg-rose-100 text-rose-600';
      case 'equipment_inspection': return 'bg-blue-100 text-blue-600';
      case 'job_hazard_analysis': return 'bg-amber-100 text-amber-600';
      case 'jobsite_inspection': return 'bg-emerald-100 text-emerald-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'closed': return 'badge-success';
      case 'reviewed': return 'badge-warning';
      default: return 'badge-neutral';
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical': return 'badge-danger';
      case 'high': return 'badge-danger';
      case 'medium': return 'badge-warning';
      default: return 'badge-neutral';
    }
  };

  const filteredSubmissions = submissions.filter(sub =>
    sub.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sub.submitted_by_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="form-submissions-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
            Form Submissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Review and manage all form submissions.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search submissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-submissions"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48" data-testid="filter-type">
              <SelectValue placeholder="Form Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="incident">Incident</SelectItem>
              <SelectItem value="equipment_inspection">Equipment Inspection</SelectItem>
              <SelectItem value="job_hazard_analysis">Job Hazard Analysis</SelectItem>
              <SelectItem value="jobsite_inspection">Jobsite Inspection</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Submissions List */}
        {filteredSubmissions.length > 0 ? (
          <div className="space-y-4">
            {filteredSubmissions.map((sub) => (
              <Card key={sub.submission_id} className="hover:border-slate-300 transition-colors" data-testid={`submission-${sub.submission_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${getFormColor(sub.form_type)} flex items-center justify-center flex-shrink-0`}>
                        {getFormIcon(sub.form_type)}
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-primary">{sub.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          Submitted by {sub.submitted_by_name} on {new Date(sub.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {sub.location && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Location: {sub.location}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {sub.severity && (
                        <Badge className={getSeverityBadge(sub.severity)}>
                          {sub.severity}
                        </Badge>
                      )}
                      <Badge className={getStatusBadge(sub.status)}>
                        {sub.status}
                      </Badge>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedSubmission(sub);
                          setReviewStatus(sub.status);
                          setReviewNotes(sub.notes || '');
                        }}
                        data-testid={`view-${sub.submission_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <ClipboardList className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-heading text-lg font-semibold text-primary mb-2">
                No Submissions Found
              </h3>
              <p className="text-muted-foreground">
                {searchQuery || filterType !== 'all' || filterStatus !== 'all'
                  ? 'Try adjusting your filters'
                  : 'No form submissions have been made yet'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Submission Detail Dialog */}
        <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${getFormColor(selectedSubmission?.form_type)} flex items-center justify-center`}>
                  {getFormIcon(selectedSubmission?.form_type)}
                </div>
                {selectedSubmission?.title}
              </DialogTitle>
            </DialogHeader>
            
            {selectedSubmission && (
              <div className="space-y-6">
                {/* Meta info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-secondary rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Submitted By</p>
                    <p className="font-medium">{selectedSubmission.submitted_by_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date</p>
                    <p className="font-medium">{new Date(selectedSubmission.created_at).toLocaleString()}</p>
                  </div>
                  {selectedSubmission.location && (
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{selectedSubmission.location}</p>
                    </div>
                  )}
                  {selectedSubmission.severity && (
                    <div>
                      <p className="text-sm text-muted-foreground">Severity</p>
                      <Badge className={getSeverityBadge(selectedSubmission.severity)}>
                        {selectedSubmission.severity}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Form Data */}
                <div>
                  <h4 className="font-medium text-primary mb-3">Form Responses</h4>
                  <div className="space-y-3">
                    {Object.entries(selectedSubmission.data || {}).map(([key, value]) => (
                      <div key={key} className="p-3 bg-secondary rounded-lg">
                        <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="font-medium mt-1">
                          {Array.isArray(value) ? value.join(', ') : 
                           typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
                           value || '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Review Section */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-primary mb-3">Review & Update</h4>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1.5">Status</p>
                      <Select value={reviewStatus} onValueChange={setReviewStatus}>
                        <SelectTrigger data-testid="review-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="reviewed">Reviewed</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1.5">Notes</p>
                      <Textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Add review notes..."
                        data-testid="review-notes"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={() => setSelectedSubmission(null)}>
                        Cancel
                      </Button>
                      <Button onClick={handleReview} data-testid="save-review-btn">
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FormSubmissions;
